import React, { useState, useMemo, useRef, useEffect } from 'react';
import { GoogleGenAI, Type } from "@google/genai";
import { type Room, type Tenant, type RoomMember, type ResidencyRegistration, type BoardingHouseInfo, RoomStatus, ResidencyStatus } from '../types';
import Icon from './ui/Icon';
import ConfirmationModal from './ui/ConfirmationModal';
import ContractModal from './ContractModal';
import CT01Modal from './CT01Modal';
import { formatDateForInput } from '../utils/dateUtils';

interface RoomChoiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddMember: () => void;
    onReplace: () => void;
    room: Room | null;
    occupant: Tenant | null;
    newTenantName: string;
}

const RoomChoiceModal: React.FC<RoomChoiceModalProps> = ({ isOpen, onClose, onAddMember, onReplace, room, occupant, newTenantName }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[80] p-4" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4">Phòng {room?.roomNumber} đã có người thuê</h3>
                <p className="mb-6 text-gray-600 dark:text-gray-300">
                    Phòng {room?.roomNumber} hiện đang được thuê bởi <strong>{occupant?.name}</strong>. Bạn muốn:
                </p>
                <div className="flex flex-col space-y-3">
                     <button onClick={onReplace} className="w-full px-4 py-3 font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-500">
                        Thay thế khách thuê hiện tại
                     </button>
                     <button onClick={onAddMember} className="w-full px-4 py-3 font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
                        Thêm <span className="font-semibold">{newTenantName}</span> làm thành viên
                    </button>
                </div>
                 <div className="text-center mt-4">
                    <button onClick={onClose} className="text-sm text-gray-500 hover:underline">Hủy</button>
                </div>
            </div>
        </div>
    );
};




const getStatusBadge = (status: ResidencyStatus) => {
    switch (status) {
        case ResidencyStatus.Registered:
            return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">Đã đăng ký</span>;
        case ResidencyStatus.Expired:
            return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300">Hết hạn</span>;
        case ResidencyStatus.Unregistered:
        default:
            return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">Chưa đăng ký</span>;
    }
};

const AddEditTenantModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSave: (tenant: Tenant) => void;
    existingTenant: Tenant | null;
    rooms: Room[];
    tenants: Tenant[];
    allPeople: any[];
    geminiApiKey: string;
}> = ({ isOpen, onClose, onSave, existingTenant, rooms, tenants, allPeople, geminiApiKey }) => {
    const [formData, setFormData] = useState<Partial<Tenant>>({});
    const [isExtracting, setIsExtracting] = useState(false);
    const [extractionError, setExtractionError] = useState<string | null>(null);
    const hasApiKey = geminiApiKey && geminiApiKey.trim() !== '';

    useEffect(() => {
        if (existingTenant) {
            setFormData(existingTenant);
        } else {
            const firstAvailableRoom = rooms.find(r => r.status === RoomStatus.Available);
            setFormData({
                name: '',
                gender: 'Nam',
                idType: 'CCCD',
                idNumber: '',
                phone: '',
                permanentAddress: '',
                dateOfBirth: '',
                idIssueDate: '',
                idIssuePlace: '',
                roomId: firstAvailableRoom ? firstAvailableRoom.id : '',
                contractStartDate: new Date().toISOString().split('T')[0],
                depositAmount: 0,
            });
        }
        setExtractionError(null);
    }, [existingTenant, rooms, isOpen]);

    if (!isOpen) return null;

    const retryWithBackoff = async (fn: () => Promise<any>, maxRetries: number = 3, baseDelay: number = 1000): Promise<any> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error: any) {
            // Check if it's a quota exceeded error
            if (error.status === 429 && attempt < maxRetries) {
                const retryDelay = baseDelay * Math.pow(2, attempt - 1); // Exponential backoff
                console.warn(`API quota exceeded. Retrying in ${retryDelay/1000}s... (Attempt ${attempt}/${maxRetries})`);
                await new Promise(resolve => setTimeout(resolve, retryDelay));
                continue;
            }
            throw error;
        }
    }
};

const detectCCCD_Sides = async (files: FileList, ai: any): Promise<Array<{file: File, isFront: boolean, confidence: number}>> => {
    const results = [];
    
    for (let i = 0; i < Math.min(files.length, 2); i++) {
        const file = files[i];
        const base64String = await readFileAsBase64(file);
        
        try {
            const mimeType = file.type === 'application/pdf' ? 'application/pdf' : 
                            file.type.startsWith('image/') ? file.type : 'image/jpeg';
            
            const response = await retryWithBackoff(async () => {
                return await ai.models.generateContent({
                    model: "gemini-2.5-flash",
                    contents: {
                        parts: [
                            { inlineData: { mimeType, data: base64String } },
                            { text: `Phân tích hình CCCD này và xác determin nó là MÁT TRUÓC hay MÁT SAU.
                            
                            MÁT TRUÓC có các dáu hiáu:
                            - Chúa só CCCD (góc trái trên)
                            - Chúa h và tên (giáa)
                            - Chúa ngày sinh, giói tính, quê quán
                            - Có logo "CÓNG HÒA XÃ HÓI CHÚ NGHÃA VIET NAM"
                            - Thuóng có chùm ánh
                            
                            MÁT SAU có các dáu hiáu:
                            - Chúa ngày cáp, nói cáp (giáa)
                            - Chúa dáu vân tay
                            - Chúa thông tin vé só, ngày háu hét
                            - Có QR code hoác mã vach
                            - Có chùm ánh
                            
                            Trá vá JSON có các trýòng: isFront (boolean), confidence (só 0-100), reason (lí do) 
                            Ví du: {"isFront": true, "confidence": 95, "reason": "Tháy só CCCD và h và tên"}` }
                        ]
                    },
                    config: {
                        responseMimeType: "application/json",
                        responseSchema: {
                            type: Type.OBJECT,
                            properties: {
                                isFront: { type: Type.BOOLEAN, description: "true cho mát tró, false cho mát sau" },
                                confidence: { type: Type.NUMBER, description: "Múc tinhdáy 0-100" },
                                reason: { type: Type.STRING, description: "Lí do quyát quyát" }
                            },
                            required: ["isFront", "confidence", "reason"]
                        }
                    }
                });
            }, 2, 2000); // 2 retries with 2s base delay

            const text = typeof response.text === 'string' ? response.text : (response.text ?? '');
            if (!text) throw new Error('Empty response from side detection');
            const detectionResult = JSON.parse(text);
            
            results.push({
                file: file,
                isFront: detectionResult.isFront,
                confidence: detectionResult.confidence || 0
            });
            
            console.log(`File ${i + 1} side detection:`, detectionResult);
            
        } catch (error: any) {
            console.error(`Side detection failed for file ${i + 1}:`, error);
            
            // Check if it's a quota exceeded error
            if (error.status === 429) {
                setExtractionError("API Gemini dã hét lóat trong ngày. Vui lòng thá lái sau vài phút hoác nâng câp lên gói trá phí.");
                throw error; // Re-throw to stop processing
            }
            
            // Fallback: assume first file is front, second is back
            results.push({
                file: file,
                isFront: i === 0,
                confidence: 0
            });
        }
    }
    
    return results;
};

const normalizeGender = (gender: string | undefined): 'Nam' | 'Nú' | 'Khác' | undefined => {
    if (!gender) return undefined;
    
    const cleanGender = gender.trim().toLowerCase();
    
    // Handle various Vietnamese gender formats
    if (cleanGender.includes('nam') || cleanGender.includes('male') || cleanGender.includes('m')) {
        return 'Nam';
    }
    if (cleanGender.includes('n\u1ef1') || cleanGender.includes('female') || cleanGender.includes('f')) {
        return 'N\u1ef1';
    }
    
    // Return 'Khác' for unrecognized patterns
    return 'Khác';
};

const handleIdCardUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setIsExtracting(true);
        setExtractionError(null);

        try {
            const ai = new GoogleGenAI({ apiKey: geminiApiKey });
            let combinedData: any = {};
            
            // First, detect which files are front vs back sides
            const sideDetectionResults = await detectCCCD_Sides(files, ai);
            console.log('Side detection results:', sideDetectionResults);
            
            // Validate side detection results
            const frontSide = sideDetectionResults.find(r => r.isFront);
            const backSide = sideDetectionResults.find(r => !r.isFront);
            
            if (!frontSide && !backSide) {
                throw new Error('Không xác minh duóc mát tró hoác mát sau cúa CCCD. Vui lòng kiám tra lái hình ánh.');
            }
            
            // Process each file with correct side identification
            for (let i = 0; i < Math.min(files.length, 2); i++) {
                const file = files[i];
                const sideInfo = sideDetectionResults[i];
                const base64String = await readFileAsBase64(file);
                
                const responseSchema = {
                  type: Type.OBJECT,
                  properties: {
                    idNumber: { type: Type.STRING, description: "Số Căn cước công dân" },
                    fullName: { type: Type.STRING, description: "Họ và tên đầy đủ, viết hoa không dấu" },
                    dateOfBirth: { type: Type.STRING, description: "Ngày sinh theo định dạng dd/mm/yyyy" },
                    gender: { type: Type.STRING, description: "Giới tính, 'Nam' hoặc 'Nữ'" },
                    permanentAddress: { type: Type.STRING, description: "Địa chỉ nơi thường trú" },
                    idIssueDate: { type: Type.STRING, description: "Ngày cấp thẻ, theo định dạng dd/mm/yyyy. Nếu không tìm thấy, trả về chuỗi rỗng." },
                    idIssuePlace: { type: Type.STRING, description: "Nơi cấp thẻ. VD: Cục Cảnh sát quản lý hành chính về trật tự xã hội" },
                    ethnicity: { type: Type.STRING, description: "Dân tộc (nếu có)" },
                    religion: { type: Type.STRING, description: "Tôn giáo (nếu có)" },
                    features: { type: Type.STRING, description: "Dấu đặc nhận dạng (nếu có)" }
                  },
                  required: ["idNumber", "fullName", "dateOfBirth", "gender", "permanentAddress"]
                };

                const mimeType = file.type === 'application/pdf' ? 'application/pdf' : 
                                file.type.startsWith('image/') ? file.type : 'image/jpeg';
                
                const promptText = `Trích xuất thông tin từ ${file.type === 'application/pdf' ? 'PDF' : 'hình ảnh'} Căn cước công dân này. ${sideInfo.isFront ? 'Mặt trước' : 'Mặt sau'}. 
                Họ và tên phải được chuyển thành chữ không dấu. 
                ${sideInfo.isFront ? 'Mặt trước chứa: số CCCD, họ và tên, ngày sinh, giới tính, quê quán (nơi thường trú).' : 'Mặt sau chứa: ngày cấp, nơi cấp, dân tộc, tôn giáo, dấu đặc nhận dạng, và có thể chứa thêm thông tin từ mặt trước.'}
                QUAN TRỌNG: 
                - ${sideInfo.isFront ? 'Tìm kiếm số CCCD, họ và tên, ngày sinh, giới tính, và địa chỉ thường trú' : 'Tìm kiếm ngày cấp, nơi cấp, dân tộc, tôn giáo, dấu đặc nhận dạng, ngày hết hạn, và dấu vân tay'}
                - ${sideInfo.isFront ? 'Nếu tìm thấy ngày cấp hoặc nơi cấp, có thể là mặt sau' : 'Ngày cấp và nơi cấp luôn có trong mặt sau'}
                - Trả về dấu liệu chính xác nhất có thể`;
                
                const response = await retryWithBackoff(async () => {
                return await ai.models.generateContent({
                    model: "gemini-2.5-flash",
                    contents: {
                        parts: [
                            { inlineData: { mimeType, data: base64String } },
                            { text: promptText }
                        ]
                    },
                    config: {
                        responseMimeType: "application/json",
                        responseSchema: responseSchema,
                    }
                });
            }, 2, 2000); // 2 retries with 2s base delay

                const text = typeof response.text === 'string' ? response.text : (response.text ?? '');
                if (!text) throw new Error('Empty response from OCR');
                const extractedData = JSON.parse(text);
                
                // Debug: Log extracted data for each side
                console.log(`CCCD ${sideInfo.isFront ? 'FRONT' : 'BACK'} extracted:`, extractedData);
                
                // Merge data with smart prioritization based on side
                Object.keys(extractedData).forEach(key => {
                    const value = extractedData[key];
                    if (value && value.trim() !== '') {
                        // Front side prioritized for personal info
                        if (sideInfo.isFront && ['idNumber', 'fullName', 'dateOfBirth', 'gender', 'permanentAddress'].includes(key)) {
                            combinedData[key] = value;
                        }
                        // Back side prioritized for issue info
                        else if (!sideInfo.isFront && ['idIssueDate', 'idIssuePlace', 'ethnicity', 'religion', 'features'].includes(key)) {
                            combinedData[key] = value;
                        }
                        // Fallback: use if not already set
                        else if (!combinedData[key]) {
                            combinedData[key] = value;
                        }
                    }
                });
            }

            // Debug: Log final combined data
            console.log('Final combined CCCD data:', combinedData);
            
            const updatedFormData = {
                ...formData,
                name: combinedData.fullName || formData.name,
                idNumber: combinedData.idNumber || formData.idNumber,
                dateOfBirth: formatDateForInput(combinedData.dateOfBirth) || formData.dateOfBirth,
                gender: normalizeGender(combinedData.gender) || formData.gender,
                permanentAddress: combinedData.permanentAddress || formData.permanentAddress,
                idIssueDate: formatDateForInput(combinedData.idIssueDate) || formData.idIssueDate,
                idIssuePlace: combinedData.idIssuePlace || formData.idIssuePlace,
            };
            
            console.log('Updated form data:', updatedFormData);
            setFormData(updatedFormData);
        } catch (error: any) {
            import('../utils/logger').then(l => l.error('Lôí khi trích xuát thông tin CCCD:', error));
            
            // Check if it's a quota exceeded error
            if (error.status === 429) {
                setExtractionError("API Gemini dã hét lóat trong ngày. Vui lòng thá lái sau vài phút hoác nâng câp lên gói trá phí.");
            } else {
                setExtractionError("Không thê trích xuát thông tin. Vui lòng thá lái hoác nháp tay công.");
            }
        } finally {
            setIsExtracting(false);
            event.target.value = '';
        }
    };

    const readFileAsBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                resolve(result.split(',')[1]);
            };
            reader.onerror = () => reject(new Error("Không thể đọc tệp."));
            reader.readAsDataURL(file);
        });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleIdBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const idNumber = e.target.value.trim();
        if (!idNumber) return;
        if (existingTenant && existingTenant.idNumber === idNumber) return;
        const existingPerson = allPeople.find(p => p.idNumber === idNumber);
        if (existingPerson) {
            setFormData(prev => ({ ...prev, ...existingPerson }));
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const tenantData: Tenant = { id: existingTenant?.id || `T${Date.now()}`, ...formData } as Tenant;
        onSave(tenantData);
    };
    
    const getRoomLabel = (room: Room) => {
        let label = `Phòng ${room.roomNumber}`;
        if (room.status === RoomStatus.Occupied && room.tenantId) {
            const occupant = tenants.find(t => t.id === room.tenantId);
            if (occupant && occupant.id !== existingTenant?.id) {
                label += ` (Đã thuê: ${occupant.name})`;
            }
        } else if (room.status === RoomStatus.Maintenance) {
            label += ` (Bảo trì)`;
        }
        return label;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[60] p-4 sm:p-0">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl h-full sm:h-auto sm:max-h-[95vh] flex flex-col">
                <form onSubmit={handleSubmit} className="flex flex-col h-full">
                    <div className="flex-shrink-0 p-6 border-b"><h2 className="text-xl font-semibold">{existingTenant ? 'Chinh súa khách thuê' : 'Thêm khách thuê mói'}</h2></div>
                    <div className="flex-grow overflow-y-auto p-6 space-y-4">
                        <div>
                            <label htmlFor={hasApiKey ? "id-card-upload" : undefined} className={`w-full flex items-center justify-center px-4 py-3 border-2 border-dashed rounded-lg ${hasApiKey ? 'cursor-pointer text-primary-600 border-primary-400 hover:bg-primary-50' : 'cursor-not-allowed text-gray-400 border-gray-300 bg-gray-100'}`}>
                                {isExtracting ? 'Ðang phân tích và trích xuát...' : <><Icon name="camera" className="h-6 w-6 mr-2" /><span>Quét CCCD (2 mát) & dièn tá dáng</span></>}
                            </label>
                            <input type="file" id="id-card-upload" className="hidden" accept="image/*,.pdf" onChange={handleIdCardUpload} disabled={!hasApiKey} multiple capture="environment" />
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Hô trá nhiáu hình ánh hoác PDF, quét cà 2 mát CCCD</p>
                            {extractionError && <p className="mt-2 text-sm text-red-500">{extractionError}</p>}
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <input type="text" name="name" value={formData.name || ''} onChange={handleChange} placeholder="Hó và tên" className="input-field" required />
                            <select name="gender" value={formData.gender || 'Nam'} onChange={handleChange} className="input-field"><option value="Nam">Nam</option><option value="Nú">Nú</option><option value="Khác">Khác</option></select>
                        </div>
                        <div>
                            <label className="label-field">Thông tin CCCD/CMND</label>
                            <div className="grid md:grid-cols-3 gap-4 mt-1">
                                <input type="text" name="idNumber" value={formData.idNumber || ''} onChange={handleChange} onBlur={handleIdBlur} placeholder="Só CMND/CCCD" className="input-field" required />
                                <input type="date" name="idIssueDate" value={formData.idIssueDate || ''} onChange={handleChange} className="input-field" />
                                <input type="text" name="idIssuePlace" value={formData.idIssuePlace || ''} onChange={handleChange} placeholder="Nói cáp" className="input-field" />
                            </div>
                        </div>
                        <input type="tel" name="phone" value={formData.phone || ''} onChange={handleChange} placeholder="Số điện thoại" className="input-field" />
                        <div><label className="label-field">Ngày sinh</label><input type="date" name="dateOfBirth" value={formData.dateOfBirth || ''} onChange={handleChange} className="input-field" /></div>
                        <input type="text" name="permanentAddress" value={formData.permanentAddress || ''} onChange={handleChange} placeholder="Địa chỉ thường trú" className="input-field" />
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="label-field">Phòng thuê</label>
                                <select name="roomId" value={formData.roomId || ''} onChange={handleChange} className="input-field" required>
                                    <option value="">Chọn phòng</option>
                                    {rooms.map(room => <option key={room.id} value={room.id}>{getRoomLabel(room)}</option>)}
                                </select>
                            </div>
                            <div><label className="label-field">Tiền đặt cọc (VND)</label><input type="number" name="depositAmount" value={formData.depositAmount || 0} onChange={handleChange} className="input-field" /></div>
                        </div>
                        <div><label className="label-field">Ngày bắt đầu hợp đồng</label><input type="date" name="contractStartDate" value={formData.contractStartDate || ''} onChange={handleChange} className="input-field" /></div>
                    </div>
                    <div className="flex-shrink-0 flex justify-end space-x-4 p-6 border-t"><button type="button" onClick={onClose} className="btn-secondary">Hủy</button><button type="submit" className="btn-primary">{existingTenant ? 'Lưu thay đổi' : 'Thêm khách thuê'}</button></div>
                </form>
            </div>
        </div>
    );
};

const EditMemberModal: React.FC<{ isOpen: boolean; onClose: () => void; onSave: (member: RoomMember) => void; member: RoomMember | null; }> = ({ isOpen, onClose, onSave, member }) => {
    const [formData, setFormData] = useState<RoomMember | null>(member);
    useEffect(() => { setFormData(member); }, [member]);
    if (!isOpen || !formData) return null;
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => prev ? { ...prev, [name]: value } : null);
    };
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (formData) onSave(formData);
    };
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[70] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg flex flex-col">
                <datalist id="relationship-options">
                    <option value="Vợ" />
                    <option value="Chồng" />
                    <option value="Con gái" />
                    <option value="Con trai" />
                    <option value="Bá" />
                    <option value="Má" />
                    <option value="Ông" />
                    <option value="Bà" />
                    <option value="Anh" />
                    <option value="Chị" />
                    <option value="Em" />
                    <option value="Cô" />
                    <option value="Chú" />
                    <option value="Bác" />
                    <option value="Dì" />
                    <option value="Cáu" />
                    <option value="Cháu" />
                    <option value="Bạn thân" />
                    <option value="Hàng xóm" />
                    <option value="Người cùng phòng" />
                    <option value="Khác" />
                </datalist>
                <form onSubmit={handleSubmit} className="flex flex-col h-full">
                    <div className="p-6 border-b"><h2 className="text-xl font-semibold">Chỉnh sửa thông tin thành viên</h2></div>
                    <div className="p-6 space-y-4 overflow-y-auto">
                        <div className="grid md:grid-cols-2 gap-4">
                            <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Họ và tên" className="input-field" required />
                            <select name="gender" value={formData.gender} onChange={handleChange} className="input-field"><option value="Nam">Nam</option><option value="Nữ">Nữ</option><option value="Khác">Khác</option></select>
                        </div>
                        <input type="text" name="idNumber" value={formData.idNumber} onChange={handleChange} placeholder="Số CMND/CCCD" className="input-field" />
                        <input type="tel" name="phone" value={formData.phone || ''} onChange={handleChange} placeholder="Số điện thoại" className="input-field" />
                        <div><label className="label-field">Ngày sinh</label><input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} className="input-field"/></div>
                        <input type="text" name="relationship" value={formData.relationship} onChange={handleChange} placeholder="Quan hệ với chủ phòng" className="input-field" list="relationship-options"/>
                    </div>
                    <div className="p-6 border-t flex justify-end space-x-4"><button type="button" onClick={onClose} className="btn-secondary">Hủy</button><button type="submit" className="btn-primary">Lưu thay đổi</button></div>
                </form>
            </div>
        </div>
    );
};

interface TenantManagementProps {
    tenants: Tenant[];
    setTenants: React.Dispatch<React.SetStateAction<Tenant[]>>;
    rooms: Room[];
    setRooms: React.Dispatch<React.SetStateAction<Room[]>>;
    roomMembers: RoomMember[];
    setRoomMembers: React.Dispatch<React.SetStateAction<RoomMember[]>>;
    residencyRegistrations: ResidencyRegistration[];
    setResidencyRegistrations: React.Dispatch<React.SetStateAction<ResidencyRegistration[]>>;
    allPeople: any[];
    geminiApiKey: string;
    boardingHouseInfo: BoardingHouseInfo;
}

interface UnifiedPerson {
  id: string;
  name: string;
  role: 'Chủ phòng' | 'Thành viên';
  roomNumber: string;
  phone: string;
  contractEndDate: string | undefined;
  isMember: boolean;
  originalData: Tenant | RoomMember;
  residencyStatus: ResidencyStatus;
}

const TenantManagement: React.FC<TenantManagementProps> = ({ tenants, setTenants, rooms, setRooms, roomMembers, setRoomMembers, residencyRegistrations, setResidencyRegistrations, allPeople, geminiApiKey, boardingHouseInfo }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [tenantToEdit, setTenantToEdit] = useState<Tenant | null>(null);
    const [isContractModalOpen, setIsContractModalOpen] = useState(false);
    const [isCT01ModalOpen, setIsCT01ModalOpen] = useState(false);
    const [isChoiceModalOpen, setIsChoiceModalOpen] = useState(false);
    const [choiceContext, setChoiceContext] = useState<{ tenantData: Tenant; room: Room; occupant: Tenant; } | null>(null);
    const [isEditMemberModalOpen, setIsEditMemberModalOpen] = useState(false);
    const [memberToEdit, setMemberToEdit] = useState<RoomMember | null>(null);
    const [docContext, setDocContext] = useState<{ mainPerson: Tenant | RoomMember | null; accompanyingPeople: (Tenant | RoomMember)[]; room: Room | null; }>({ mainPerson: null, accompanyingPeople: [], room: null });
    const [isConfirmOpen, setConfirmOpen] = useState(false);
    const [confirmProps, setConfirmProps] = useState({ title: '', message: '', onConfirm: () => {} });

    const unifiedPeopleList: UnifiedPerson[] = useMemo(() => {
        const people: UnifiedPerson[] = [];
        tenants.forEach(tenant => {
            if (!tenant.roomId) return; 
            const room = rooms.find(r => r.id === tenant.roomId);
            const registration = residencyRegistrations.find(r => r.personId === tenant.id);
            people.push({ id: tenant.id, name: tenant.name, role: 'Chủ phòng', roomNumber: room?.roomNumber || 'N/A', phone: tenant.phone, contractEndDate: tenant.contractEndDate, isMember: false, originalData: tenant, residencyStatus: registration ? registration.status : ResidencyStatus.Unregistered });
        });
        roomMembers.forEach(member => {
            const mainTenant = tenants.find(t => t.id === member.tenantId);
            if (!mainTenant || !mainTenant.roomId) return;
            const room = rooms.find(r => r.id === mainTenant.roomId);
            const registration = residencyRegistrations.find(r => r.personId === member.id);
            people.push({ id: member.id, name: member.name, role: 'Thành viên', roomNumber: room?.roomNumber || 'N/A', phone: member.phone, contractEndDate: mainTenant.contractEndDate, isMember: true, originalData: member, residencyStatus: registration ? registration.status : ResidencyStatus.Unregistered });
        });
        return people.sort((a, b) => parseInt(a.roomNumber, 10) - parseInt(b.roomNumber, 10) || (a.role === 'Chủ phòng' ? -1 : 1) || a.name.localeCompare(b.name));
    }, [tenants, roomMembers, rooms, residencyRegistrations]);

    const handleOpenAddTenantModal = () => { setTenantToEdit(null); setIsModalOpen(true); };
    const handleCloseModal = () => { setTenantToEdit(null); setIsModalOpen(false); };
    
    const handleOpenContract = (person: UnifiedPerson) => {
        const room = rooms.find(r => r.roomNumber === person.roomNumber);
        if (!room) return;
        const mainTenant = person.isMember ? tenants.find(t => t.id === (person.originalData as RoomMember).tenantId) : person.originalData as Tenant;
        if (!mainTenant) return;

        setDocContext({ mainPerson: mainTenant, room, accompanyingPeople: [] });
        setIsContractModalOpen(true);
    };

    const handleOpenCT01 = (person: UnifiedPerson) => {
        const mainPerson = person.originalData;
        const tenantId = person.isMember ? (mainPerson as RoomMember).tenantId : mainPerson.id;
        const mainTenant = tenants.find(t => t.id === tenantId);
        if(!mainTenant) return;
        const room = rooms.find(r => r.id === mainTenant.roomId);
        const membersInRoom = roomMembers.filter(m => m.tenantId === tenantId);
        const allPeopleInRoom = [mainTenant, ...membersInRoom];
        const accompanyingPeople = allPeopleInRoom.filter(p => p.id !== mainPerson.id);
        
        setDocContext({ mainPerson, room: room ?? null, accompanyingPeople });
        setIsCT01ModalOpen(true);
    };

    const proceedWithSave = (tenantData: Tenant, fromRoomId: string | undefined, tenantToEvictId: string | undefined) => {
        const toRoomId = tenantData.roomId;
        setRooms(prevRooms => {
            let newRooms = [...prevRooms];
            // Vacate the old room if moving
            if (fromRoomId && fromRoomId !== toRoomId) {
                const fromRoomIndex = newRooms.findIndex(r => r.id === fromRoomId);
                if (fromRoomIndex > -1) newRooms[fromRoomIndex] = { ...newRooms[fromRoomIndex], status: RoomStatus.Available, tenantId: undefined };
            }
            // Occupy the new room
            const toRoomIndex = newRooms.findIndex(r => r.id === toRoomId);
            if (toRoomIndex > -1) {
                newRooms[toRoomIndex] = { ...newRooms[toRoomIndex], status: RoomStatus.Occupied, tenantId: tenantData.id };
            }
            return newRooms;
        });
        
        setTenants(prevTenants => {
            // Evict old tenant if replacing
            let newTenants = tenantToEvictId ? prevTenants.filter(t => t.id !== tenantToEvictId) : [...prevTenants];
            // Add or update the new tenant
            const isNew = !prevTenants.some(t => t.id === tenantData.id);
            if (isNew) {
                newTenants.push(tenantData);
            } else {
                newTenants = newTenants.map(t => t.id === tenantData.id ? tenantData : t);
            }
            return newTenants;
        });

        if (tenantToEvictId) {
            const peopleIdsToDelete = [tenantToEvictId, ...roomMembers.filter(m => m.tenantId === tenantToEvictId).map(m => m.id)];
            setRoomMembers(prev => prev.filter(m => m.tenantId !== tenantToEvictId));
            setResidencyRegistrations(prev => prev.filter(reg => !peopleIdsToDelete.includes(reg.personId)));
        }
    };
    
    const handleSaveTenant = (tenantData: Tenant) => {
        const isNewTenant = !tenants.some(t => t.id === tenantData.id);
        const fromRoomId = tenants.find(t => t.id === tenantData.id)?.roomId;

        // Simple update, no room change
        if (!isNewTenant && fromRoomId === tenantData.roomId) {
            setTenants(prev => prev.map(t => t.id === tenantData.id ? tenantData : t));
            handleCloseModal();
            return;
        }

        const toRoom = rooms.find(r => r.id === tenantData.roomId);
        const tenantToEvictId = toRoom?.tenantId;

        // New room is occupied, ask user what to do
        if (tenantToEvictId && tenantToEvictId !== tenantData.id) {
            const tenantToEvict = tenants.find(t => t.id === tenantToEvictId);
            if (tenantToEvict && toRoom) {
                setChoiceContext({ tenantData, room: toRoom, occupant: tenantToEvict });
                setIsChoiceModalOpen(true);
                return;
            }
        }
        
        // New room is available or it's a new tenant
        proceedWithSave(tenantData, fromRoomId, undefined);
        handleCloseModal();
    };
    
    const handleAddAsMember = () => {
        if (!choiceContext) return;
        const { tenantData, occupant } = choiceContext;
        const newMember: RoomMember = { id: `M${Date.now()}`, tenantId: occupant.id, name: tenantData.name, gender: tenantData.gender, idType: tenantData.idType, idNumber: tenantData.idNumber, phone: tenantData.phone, permanentAddress: tenantData.permanentAddress, dateOfBirth: tenantData.dateOfBirth, idIssueDate: tenantData.idIssueDate, relationship: 'Bạn bè' };
        setRoomMembers(prev => [...prev, newMember]);
        
        // If the added person was an existing tenant, remove their old tenancy
        if (tenants.some(t => t.id === tenantData.id)) {
            setRooms(prev => prev.map(r => r.tenantId === tenantData.id ? {...r, status: RoomStatus.Available, tenantId: undefined} : r));
            setTenants(prev => prev.filter(t => t.id !== tenantData.id));
        }

        setIsChoiceModalOpen(false);
        setChoiceContext(null);
        handleCloseModal();
    };
    
    const handleReplaceTenant = () => {
        if (!choiceContext) return;
        const { tenantData, occupant } = choiceContext;
        const fromRoomId = tenants.find(t => t.id === tenantData.id)?.roomId;
        proceedWithSave(tenantData, fromRoomId, occupant.id);
        setIsChoiceModalOpen(false);
        setChoiceContext(null);
        handleCloseModal();
    };

    const handleDeletePerson = (person: UnifiedPerson) => {
        const onConfirm = person.isMember ? () => {
            setRoomMembers(prev => prev.filter(m => m.id !== person.id));
            setResidencyRegistrations(prev => prev.filter(r => r.personId !== person.id));
        } : () => {
            const tenantId = person.id;
            const tenantToDelete = tenants.find(t => t.id === tenantId);
            if (tenantToDelete?.roomId) setRooms(rooms => rooms.map(r => r.id === tenantToDelete.roomId ? { ...r, status: RoomStatus.Available, tenantId: undefined } : r));
            const peopleIdsToDelete = [tenantId, ...roomMembers.filter(m => m.tenantId === tenantId).map(m => m.id)];
            setTenants(tenants => tenants.filter(t => t.id !== tenantId));
            setRoomMembers(members => members.filter(m => m.tenantId !== tenantId));
            setResidencyRegistrations(regs => regs.filter(reg => !peopleIdsToDelete.includes(reg.personId)));
        };
        setConfirmProps({ title: `Xác nhận xóa ${person.isMember ? 'thành viên' : 'khách thuê'}`, message: `Bạn có chắc chắn muốn xóa "${person.name}"?`, onConfirm });
        setConfirmOpen(true);
    };
    const handleOpenEditPersonModal = (person: Tenant | RoomMember) => {
        if ('roomId' in person) { setTenantToEdit(person); setIsModalOpen(true); } 
        else { setMemberToEdit(person); setIsEditMemberModalOpen(true); }
    };
    const handleCloseEditMemberModal = () => { setMemberToEdit(null); setIsEditMemberModalOpen(false); };
    const handleSaveMember = (updatedMember: RoomMember) => {
        setRoomMembers(prev => prev.map(m => m.id === updatedMember.id ? updatedMember : m));
        handleCloseEditMemberModal();
    };

    return (
        <>
        <ConfirmationModal isOpen={isConfirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={() => { confirmProps.onConfirm(); setConfirmOpen(false); }} title={confirmProps.title}>{confirmProps.message}</ConfirmationModal>
        {isChoiceModalOpen && choiceContext && <RoomChoiceModal isOpen={isChoiceModalOpen} onClose={() => { setIsChoiceModalOpen(false); setChoiceContext(null); }} onAddMember={handleAddAsMember} onReplace={handleReplaceTenant} room={choiceContext.room} occupant={choiceContext.occupant} newTenantName={choiceContext.tenantData.name} />}
        <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-lg">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
                <h2 className="text-xl font-semibold">Toàn bộ người cư trú</h2>
                <button onClick={handleOpenAddTenantModal} className="btn-primary">Thêm khách thuê</button>
            </div>
            
            <div className="overflow-x-auto hidden md:block">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs uppercase bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-4 py-3">Họ và tên</th><th className="px-4 py-3">Vai trò</th><th className="px-4 py-3">Số phòng</th><th className="px-4 py-3">Cư trú</th><th className="px-4 py-3">Hết hạn HĐ</th><th className="px-4 py-3 text-right">Hành động</th>
                        </tr>
                    </thead>
                    <tbody>
                        {unifiedPeopleList.map(person => (
                            <tr key={person.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                                <td className="px-4 py-4 font-medium">{person.name}</td>
                                <td className="px-4 py-4"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${person.isMember ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300' : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'}`}>{person.role}</span></td>
                                <td className="px-4 py-4">{person.roomNumber}</td>
                                <td className="px-4 py-4">{getStatusBadge(person.residencyStatus)}</td>
                                <td className="px-4 py-4">{person.contractEndDate}</td>
                                <td className="px-4 py-4 text-right space-x-2 whitespace-nowrap">
                                    <button onClick={() => handleOpenContract(person)} className="font-medium text-green-600 dark:text-green-500 hover:underline">Hợp đồng</button>
                                    <button onClick={() => handleOpenCT01(person)} className="font-medium text-blue-600 dark:text-blue-500 hover:underline">CT01</button>
                                    <button onClick={() => handleOpenEditPersonModal(person.originalData)} className="font-medium text-primary-600 dark:text-primary-500 hover:underline">Sửa</button>
                                    <button onClick={() => handleDeletePerson(person)} className="font-medium text-red-600 dark:text-red-500 hover:underline">Xóa</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

             <div className="grid sm:grid-cols-2 gap-4 md:hidden">
                {unifiedPeopleList.map(person => (
                    <div key={person.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow p-4 space-y-3">
                        <div className="flex justify-between items-start">
                            <div><p className="font-bold text-lg">{person.name}</p><p className="text-sm text-gray-600 dark:text-gray-400">Phòng {person.roomNumber}</p></div>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${person.isMember ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300' : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'}`}>{person.role}</span>
                        </div>
                        <div className="border-t dark:border-gray-700 pt-3 space-y-2 text-sm">
                            <div className="flex justify-between items-center"><span>Cư trú:</span>{getStatusBadge(person.residencyStatus)}</div>
                            <div className="flex justify-between"><span>Hết hạn HĐ:</span><span className="font-medium">{person.contractEndDate}</span></div>
                        </div>
                        <div className="flex justify-end flex-wrap gap-2 pt-2">
                            <button onClick={() => handleOpenContract(person)} className="action-btn-mobile bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-300">Hợp đồng</button>
                            <button onClick={() => handleOpenCT01(person)} className="action-btn-mobile bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300">CT01</button>
                            <button onClick={() => handleOpenEditPersonModal(person.originalData)} className="action-btn-mobile bg-primary-100 text-primary-600 dark:bg-primary-900/50 dark:text-primary-300">Sửa</button>
                            <button onClick={() => handleDeletePerson(person)} className="action-btn-mobile bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-300">Xóa</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        {isModalOpen && <AddEditTenantModal isOpen={isModalOpen} onClose={handleCloseModal} onSave={handleSaveTenant} existingTenant={tenantToEdit} rooms={rooms} tenants={tenants} allPeople={allPeople} geminiApiKey={geminiApiKey} />}
        {isEditMemberModalOpen && <EditMemberModal isOpen={isEditMemberModalOpen} onClose={handleCloseEditMemberModal} onSave={handleSaveMember} member={memberToEdit} />}
        {isContractModalOpen && docContext.mainPerson && <ContractModal tenant={docContext.mainPerson as Tenant} room={docContext.room} onClose={() => setIsContractModalOpen(false)} boardingHouseInfo={boardingHouseInfo} />}
        {isCT01ModalOpen && docContext.mainPerson && <CT01Modal mainPerson={docContext.mainPerson} room={docContext.room} accompanyingPeople={docContext.accompanyingPeople} onClose={() => setIsCT01ModalOpen(false)} boardingHouseInfo={boardingHouseInfo} />}
        </>
    );
};

export default TenantManagement;